import { Bookmark, ChevronRight, ExternalLink, Search } from "lucide-react";
import posthog from "posthog-js";
import React from "react";

import { Button } from "@/app/components/ui/button";
import { cn } from "@/app/handlers/cn.utils.js";
import { wrapLinksAndEmails } from "@/app/handlers/valueTransformation.js";

import { ApplicationDetails, isJobExpired, JobDetails } from "../utils/helpers";

interface ApplicationProcessProps {
	jobDetails: JobDetails;
	applicationDetails: ApplicationDetails;
	scrollToEmail: boolean;
	applicationRef: React.RefObject<HTMLDivElement>;
	onApply: (link: string | null) => void;
	onSave?: () => void;
	onExploreMore?: () => void;
	hasViewedExplainer?: boolean;
	hasCompletedFitCheck?: boolean;
}

export function ApplicationProcess({
	jobDetails,
	applicationDetails,
	scrollToEmail,
	applicationRef,
	onApply,
	onSave,
	onExploreMore,
	hasViewedExplainer = false,
	hasCompletedFitCheck = false,
}: ApplicationProcessProps) {
	// Track apply click with context
	const handleApplyClick = (link: string | null) => {
		// Determine apply context
		const eventName = hasCompletedFitCheck
			? "apply_clicked_after_fitcheck"
			: hasViewedExplainer
				? "apply_clicked_after_explainer"
				: "apply_clicked_direct";

		posthog.capture(eventName, {
			jobId: jobDetails?._id,
			jobTitle: jobDetails?.jobTitle,
			companyName: jobDetails?.company?.company_name,
			companyId: jobDetails?.company?._id,
			hasViewedExplainer,
			hasCompletedFitCheck,
		});

		onApply(link);
	};

	if (!jobDetails?.applicationProcess || jobDetails?.applicationProcess === "" || isJobExpired(jobDetails.closeAt)) {
		return null;
	}

	return (
		<div
			className={cn("mb-8 rounded-xl border", {
				"bg-primary-50 border-primary-200": scrollToEmail,
				"bg-white border-gray-200": !scrollToEmail,
			})}
			id="applicationProcess"
			ref={applicationRef}
		>
			{/* Commitment Zone Header */}
			<div className="p-5 border-b border-gray-100">
				<h2 className="text-lg font-semibold text-gray-900 mb-1">Ready to apply?</h2>
				<p className="text-sm text-gray-600">You&#39;ll submit your application after reviewing your details.</p>
			</div>

			{/* Application Instructions */}
			<div className="p-5">
				<h3 className="text-sm font-medium text-gray-700 mb-2">How to apply</h3>
				<div
					className="text-gray-600 font-inter text-sm content-wrapper"
					dangerouslySetInnerHTML={{
						__html: wrapLinksAndEmails(jobDetails?.applicationProcess),
					}}
				/>

				{/* Primary Apply CTA */}
				<div className="mt-6 flex flex-col sm:flex-row gap-3">
					{applicationDetails.applicationType === "URL" && (
						<Button
							onClick={() => handleApplyClick(applicationDetails.link)}
							className="bg-primary-700 hover:bg-primary-800 text-white flex-1 sm:flex-initial"
						>
							Apply for this job
							<ExternalLink className="h-4 w-4 ml-2" />
						</Button>
					)}

					{applicationDetails.applicationType === "EMAIL" && (
						<Button
							onClick={() => handleApplyClick(applicationDetails.link)}
							className="bg-primary-700 hover:bg-primary-800 text-white flex-1 sm:flex-initial"
						>
							Apply for this job
							<ChevronRight className="h-4 w-4 ml-2" />
						</Button>
					)}

					{/* Secondary Actions */}
					{onSave && (
						<Button onClick={onSave} variant="outline" size="default">
							<Bookmark className="h-4 w-4 mr-2" />
							Save for later
						</Button>
					)}
				</div>

				{/* Explore more jobs link */}
				{onExploreMore && (
					<button
						onClick={onExploreMore}
						className="mt-4 text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1 transition-colors"
					>
						<Search className="h-4 w-4" />
						Explore similar roles
					</button>
				)}
			</div>
		</div>
	);
}
